/**
 * ModalShell
 *
 * Tüm sayfalardaki standart modal iskelet yapısı.
 * Overlay + kart + renkli header + scroll'lanabilir body + footer.
 *
 * Props:
 *   icon        – header'daki emoji  (ör: "🏢")
 *   iconColor   – header gradient    (ör: "from-blue-600 to-indigo-700")
 *   title       – modal başlığı
 *   subtitle    – modal alt başlığı  (opsiyonel)
 *   onClose     – ✕ butonuna basınca çağrılır
 *   footer      – footer alanı (kaydet/iptal butonları) — React node
 *   size        – 'sm' | 'md' | 'lg' | 'xl'   (default: 'md')
 *   zIndex      – z-index class string          (default: 'z-[100]')
 *   children    – modal body içeriği
 *
 * Kullanım:
 *   <ModalShell
 *     icon="🏢"
 *     iconColor="from-blue-600 to-indigo-700"
 *     title="Yeni Firma Tanımla"
 *     subtitle="Kurumsal anlaşmalar ve iletişim detayları"
 *     onClose={onKapat}
 *     footer={
 *       <>
 *         <button onClick={onKapat}>İPTAL</button>
 *         <button onClick={handleKaydet}>KAYDET</button>
 *       </>
 *     }
 *   >
 *     <div>...form içeriği...</div>
 *   </ModalShell>
 */

import { useEffect } from 'react';

const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export default function ModalShell({
  icon,
  iconColor = 'from-blue-600 to-indigo-700',
  title,
  subtitle,
  onClose,
  footer,
  size = 'md',
  zIndex = 'z-[100]',
  children,
}) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div className={`fixed inset-0 bg-black/60 ${zIndex} flex items-center justify-center p-0 lg:p-4 backdrop-blur-md animate-fade-in leading-none`}>
      <div className={`bg-white w-full ${SIZES[size] ?? SIZES.md} max-h-[95vh] rounded-none lg:rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden border border-white/20 animate-scale-in`}>

        {/* Renkli Header */}
        <div className={`relative flex items-center justify-between px-8 py-7 bg-gradient-to-r ${iconColor} overflow-hidden text-white leading-none flex-shrink-0`}>
          <div className="absolute top-0 right-0 -mr-12 -mt-12 w-48 h-48 bg-white/10 rounded-full blur-2xl pointer-events-none" />
          <div className="relative flex items-center gap-4 leading-none min-w-0">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center text-2xl shadow-inner border border-white/30 flex-shrink-0">
              {icon}
            </div>
            <div className="min-w-0">
              <h2 className="font-black text-lg tracking-tight uppercase leading-none truncate">{title}</h2>
              {subtitle && (
                <p className="text-white/70 text-[10px] font-bold uppercase tracking-widest mt-1.5 leading-none truncate">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="relative z-10 text-white/60 hover:text-white w-10 h-10 flex items-center justify-center rounded-xl hover:bg-white/10 transition-all text-xl flex-shrink-0 ml-4"
            >
              ✕
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-8 space-y-6 no-scrollbar text-left leading-none">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="px-8 py-6 flex gap-4 justify-end border-t border-gray-50 bg-gray-50/50 leading-none flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
